import { Box, IconButton, Typography, useTheme } from "@material-ui/core";
import { Fragment, ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { EMPTY_VALUE_PLACEHOLDER } from "../../models/common/data";
import startUpper from "../../utils/start-upper";
import NavigationDown from "../icons/NavigationDown";
import NavigationUp from "../icons/NavigationUp";
import useStyles from "./use-styles";
import LyfeTooltip from "../LyfeTooltip";
import Adjust from "../icons/Adjust";
import { DataForAdjustmentProps } from "../draggable-window/DragabbleWindowRoot";
import AdjustmentTooltip from "../AdjustmentTooltip";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import OverflowTooltipTypography from "../OverflowTooltipTypography";

export interface LyfeSimpleTableProps {
	columns: Array<{
		headerName: string;
		key: string;
		headerIcon?: any;
		alignment: TableColumnAlignmentType;
		infoKey?: string;
		tdStyle?: any;
		style?: any;
	}>;
	rows: Array<{
		[key: string]: any;
		subRows?: Array<{ subRowType: SubRowType; payload: any }>;
		cellStyle?: any;
		isActionButtonDisabled?: boolean;
		isActionButtonLoading?: boolean;
		adjustmentWindowData?: DataForAdjustmentProps | null;
		actionButtonTooltipText?: string;
		isSecondActionButtonDisabled?: boolean;
		isSecondActionButtonLoading?: boolean;
		secondActionButtonTooltipText?: string | ReactElement;
		isBeingAdjusted?: boolean;
	}>;
	alternatingBackgroundColor?: string;
	divider?: boolean;
	style?: any;
	className?: string;
	boxClassName?: any;
	areSubRowsEnabled?: boolean;
	actionButtonIcon?: any;
	secondActionButtonIcon?: any;
	secondActionButtonDisabledIcon?: any;
	actionButtonCallback?: any;
	secondActionButtonCallback?: any;
	fixed?: boolean;
	noDataLocalizationKey?: string;
	rowIdKey?: string;
	isHeaderBold?: boolean;
}

export enum TableColumnAlignmentType {
	LEFT = "left",
	RIGHT = "right",
	CENTER = "center",
}

export enum SubRowType {
	SAME_COLUMNS,
	FULL_WIDTH,
}

const LyfeSimpleTable = (props: LyfeSimpleTableProps) => {
	const { fw400, fw700 } = useStyles();
	const [expandedRows, setExpandedRows] = useState<Array<number>>([]);
	const { t } = useTranslation();
	const { palette } = useTheme();
	const subRowKeySuffix = "-sub-row";

	const handleExpandRow = (rowIndex: number) => {
		if (expandedRows.includes(rowIndex)) {
			setExpandedRows(expandedRows.filter((index) => index !== rowIndex));
			return;
		}
		setExpandedRows([...expandedRows, rowIndex]);
	};

	if (!props.rows.length) {
		return (
			<Box display="flex" width="100%" className={props.boxClassName}>
				<Box style={{ opacity: 0.5, textAlign: "center" }} width="100%" minHeight={100} className={props.className}>
					<Typography style={{ lineHeight: "100px" }}>{t(props.noDataLocalizationKey || "no-data-to-display")}</Typography>
				</Box>
			</Box>
		);
	}

	return (
		<Box display="flex" width="100%" className={props.boxClassName}>
			<table
				style={{ tableLayout: props.fixed ? "fixed" : "auto", width: "100%", ...props.style }}
				cellPadding="10"
				cellSpacing="0"
				className={props.className}
			>
				<thead>
					<tr>
						{props.areSubRowsEnabled && (
							<th
								key={"dropdown"}
								style={{
									width: "40px",
									textAlign: "center",
									borderBottom: props.divider ? "1px solid #cddcdc" : undefined,
								}}
							></th>
						)}
						{props.columns.map((column, i) => (
							<th
								key={i}
								style={{
									...column.style,
									textAlign: column.alignment,
									borderBottom: props.divider ? "1px solid #cddcdc" : undefined,
								}}
							>
								<Typography
									noWrap
									variant="subtitle2"
									className={props.isHeaderBold === false ? fw400 : fw700}
									color="inherit"
								>
									{column.headerIcon && <span>{column.headerIcon}</span>}{" "}
									{startUpper(t(column.headerName)) || EMPTY_VALUE_PLACEHOLDER}{" "}
									{column.infoKey && (
										<LyfeTooltip placement="top-end" maxWidth="500px" title={t(column.infoKey)}>
											<InfoOutlinedIcon style={{ verticalAlign: "top" }} fontSize="small" color="primary" />
										</LyfeTooltip>
									)}
								</Typography>
							</th>
						))}
						{props.secondActionButtonIcon && (
							<th
								key={"second-action-button"}
								style={{
									textAlign: "center",
									borderBottom: props.divider ? "1px solid #cddcdc" : undefined,
								}}
							></th>
						)}
					</tr>
				</thead>
				<tbody>
					{props.rows.map((row, i) => (
						<Fragment key={i}>
							<tr
								id={props.rowIdKey ? `${props.rowIdKey}${i + 1}` : undefined}
								style={{
									width: "100%",
									background:
										props.alternatingBackgroundColor && i % 2 === 1 ? props.alternatingBackgroundColor : undefined,
								}}
							>
								{props.areSubRowsEnabled && (
									<td style={{ textAlign: "center" }}>
										<IconButton
											style={{ marginLeft: 2 }}
											aria-label="expand row"
											size="small"
											onClick={() => handleExpandRow(i)}
										>
											{expandedRows.includes(i) ? <NavigationUp /> : <NavigationDown />}
										</IconButton>
									</td>
								)}
								{props.columns.map((column, i) => (
									<td key={i} style={{ ...column.tdStyle, ...row.cellStyle, textAlign: column.alignment }}>
										{typeof row[column.key] === "number" || typeof row[column.key] === "string" ? (
											(column.key === "discountRate" || column.key === "discountAmount") && !!row.isAdjusted ? (
												<LyfeTooltip
													placement="top-end"
													maxWidth="500px"
													title={
														row.subRows?.length && row.subRows?.length <= 1 && row.secondActionButtonTooltipText
															? row.secondActionButtonTooltipText
															: ""
													}
												>
													<Box justifyContent={"space-between"} display="flex" alignItems="center">
														<span style={{ lineHeight: "12px" }}>{<Adjust fill="#212121" />}</span>
														<Typography
															display="inline"
															style={{
																whiteSpace: "nowrap",
																overflow: "hidden",
																textOverflow: "ellipsis",
																fontWeight: 600,
															}}
														>
															{row[column.key]}
														</Typography>
													</Box>
												</LyfeTooltip>
											) : (
												<OverflowTooltipTypography align={column.alignment}>
													{row[column.key]}
												</OverflowTooltipTypography>
											)
										) : (
											row[column.key]
										)}
									</td>
								))}
								{props.secondActionButtonIcon && (
									<td style={{ textAlign: "center" }}>
										<Box marginTop={row.isSecondActionButtonLoading ? "-44px" : "0px"}>
											{!row.isSecondActionButtonDisabled &&
												row.secondActionButtonTooltipText &&
												row.subRows?.length &&
												row.subRows?.length <= 1 && (
													<LyfeTooltip
														placement="top-end"
														maxWidth="500px"
														title={t("document-transaction-adjust")}
													>
														<div>
															<IconButton
																disabled={row.isSecondActionButtonDisabled}
																style={{ backgroundColor: row.isBeingAdjusted ? palette.grey[300] : "" }}
																onClick={() => props.secondActionButtonCallback(row.adjustmentWindowData)}
															>
																{row.isSecondActionButtonDisabled
																	? props.secondActionButtonDisabledIcon
																	: props.secondActionButtonIcon}
															</IconButton>
														</div>
													</LyfeTooltip>
												)}
										</Box>
									</td>
								)}
							</tr>
							{!!row.subRows?.length &&
								expandedRows.includes(i) &&
								row.subRows.map((subRow) => {
									return (
										<tr
											key={i + subRowKeySuffix}
											style={{
												width: "100%",
												background: "#ecf2f4",
											}}
										>
											{subRow.subRowType === SubRowType.FULL_WIDTH ? (
												<td
													colSpan={
														props.actionButtonIcon || props.secondActionButtonIcon
															? props.columns.length + 2
															: props.columns.length + 1
													}
													style={{ width: "100%", height: "50px" }}
												>
													{subRow.payload}
												</td>
											) : (
												<>
													{props.columns.map((column, columnIndex) => {
														const showAdjustment =
															subRow.payload.isAdjusted &&
															(column.key === "discountRate" || column.key === "discountAmount");
														return (
															<Fragment key={columnIndex}>
																{props.areSubRowsEnabled && columnIndex === 0 && <td />}
																<td style={{ ...column.tdStyle, textAlign: column.alignment }}>
																	{!showAdjustment && (
																		<>
																			{typeof row[column.key] === "number" ||
																			typeof row[column.key] === "string" ? (
																				<Typography noWrap>{subRow.payload[column.key]}</Typography>
																			) : (
																				<span>{subRow.payload[column.key]}</span>
																			)}
																		</>
																	)}
																	{showAdjustment && (
																		<LyfeTooltip
																			placement="top-end"
																			maxWidth="500px"
																			title={
																				<AdjustmentTooltip
																					{...subRow.payload.adjustmentTooltipProps}
																				/>
																			}
																		>
																			<Box
																				display="flex"
																				justifyContent="space-between"
																				alignItems="center"
																			>
																				<span style={{ lineHeight: "12px" }}>
																					{<Adjust fill="#212121" />}
																				</span>

																				{typeof row[column.key] === "number" ||
																				typeof row[column.key] === "string" ? (
																					<Typography
																						noWrap
																						style={{
																							fontWeight: "600",
																						}}
																					>
																						{subRow.payload[column.key]}
																					</Typography>
																				) : (
																					<span
																						style={{
																							fontWeight: "600",
																						}}
																					>
																						{subRow.payload[column.key]}
																					</span>
																				)}
																			</Box>
																		</LyfeTooltip>
																	)}
																</td>
															</Fragment>
														);
													})}
													<td style={{ textAlign: "center" }}>
														{props.secondActionButtonIcon && subRow.payload.canAdjust && (
															<LyfeTooltip
																placement="top-end"
																maxWidth="500px"
																title={t("document-transaction-adjust")}
															>
																<div>
																	<IconButton
																		disabled={!subRow.payload.canAdjust}
																		style={{
																			backgroundColor: subRow.payload.isBeingAdjusted
																				? palette.grey[300]
																				: "",
																		}}
																		onClick={() =>
																			props.secondActionButtonCallback(
																				subRow.payload.adjustmentWindowData
																			)
																		}
																	>
																		{!subRow.payload.canAdjust
																			? props.secondActionButtonDisabledIcon
																			: props.secondActionButtonIcon}
																	</IconButton>
																</div>
															</LyfeTooltip>
														)}
													</td>
												</>
											)}
										</tr>
									);
								})}
						</Fragment>
					))}
				</tbody>
			</table>
		</Box>
	);
};

export default LyfeSimpleTable;
